import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Sun, Moon, BookHalf } from "react-bootstrap-icons";

import { getAnalyticsWidget } from "../../../utils/Requests";
import { getDefaultDateRange, getWidgetTheme } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, DocsSidebarEntry } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import { DocsSidebar, ExportButton, ScheduleExportModal, WidgetCard } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import { createExportSchedule } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";

/* eslint-disable camelcase */
type EventInfoRow = {
    group_value: string | null;
    event_count: number;
    distinct_users: number;
    distinct_devices: number;
    distinct_things: number;
};

type EventInfoTotals = {
    event_count: number;
    distinct_users: number;
    distinct_devices: number;
    distinct_things: number;
};
/* eslint-enable camelcase */

type GroupType = "action" | "custom_event_trigger";

type EventInfoProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type EventInfoRawData = {
    action: { rows: EventInfoRow[]; totals: EventInfoTotals | null };
    custom_event_trigger: { rows: EventInfoRow[]; totals: EventInfoTotals | null }; // eslint-disable-line camelcase
};

type EventInfoState = {
    isLoading: boolean;
    error: string | null;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    isDarkTheme: boolean;
    selectedGroup: GroupType;
    rawData: EventInfoRawData | null;
    rows: EventInfoRow[];
    totals: EventInfoTotals | null;
    sortField: keyof EventInfoRow;
    sortDirection: "asc" | "desc";
    showScheduleModal: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
};

class EventInfo extends React.Component<EventInfoProps, EventInfoState> {
    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props: EventInfoProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();
        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            deviceType: []
        };

        this.state = {
            isLoading: false,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandIds: [],
                deviceTypes: []
            },
            isDarkTheme: true,
            selectedGroup: "action",
            rawData: null,
            rows: [],
            totals: null,
            sortField: "event_count",
            sortDirection: "desc",
            showScheduleModal: false,
            sidebarOpen: false,
            sidebarEntries: []
        };
    }

    componentDidMount(): void {
        this.fetchData();
    }

    private fetchData = async (): Promise<void> => {
        const { appliedFilters } = this.state;

        try {
            this.setState({ isLoading: true, error: null });

            const response = await getAnalyticsWidget(
                this.props.customerConfig,
                "event_info",
                appliedFilters.startDate,
                appliedFilters.endDate,
                appliedFilters.attributionId.length > 0 ? appliedFilters.attributionId : undefined,
                appliedFilters.brandId.length > 0 ? appliedFilters.brandId : undefined,
                appliedFilters.deviceType.length > 0 ? appliedFilters.deviceType : undefined
            );

            const data = response as any;
            const actionData = data?.action;
            const customEventTriggerData = data?.custom_event_trigger; // eslint-disable-line camelcase
            const { selectedGroup } = this.state;

            /* eslint-disable camelcase */
            const rawData: EventInfoRawData = {
                action: {
                    rows: Array.isArray(actionData?.rows) ? actionData.rows : [],
                    totals: actionData?.totals ?? null
                },
                custom_event_trigger: {
                    rows: Array.isArray(customEventTriggerData?.rows) ? customEventTriggerData.rows : [],
                    totals: customEventTriggerData?.totals ?? null
                }
            };
            /* eslint-enable camelcase */

            const groupData = rawData[selectedGroup];
            const rows = groupData.rows;
            const totals = groupData.totals;

            const sidebarEntries: DocsSidebarEntry[] = data?.widget ? [{
                widgetKey: "event_info",
                displayName: data.widget.display_name || "Event Info",
                description: data.widget.description || "",
                schema: data?.schema
            }] : [];

            this.setState({ rawData, rows, totals, sidebarEntries });
        } catch (err) {
            Logger.error("Error fetching event info data", err);
            this.setState({ error: "Failed to fetch event info data" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters }
        }), () => {
            this.fetchData();
        });
    };

    private handleGroupChange = (group: GroupType): void => {
        const { rawData } = this.state;
        if (rawData) {
            const groupData = rawData[group];
            this.setState({
                selectedGroup: group,
                rows: groupData.rows,
                totals: groupData.totals
            });
        } else {
            this.setState({ selectedGroup: group }, () => {
                this.fetchData();
            });
        }
    };

    private handleSort = (field: keyof EventInfoRow): void => {
        this.setState(prevState => ({
            sortField: field,
            sortDirection: prevState.sortField === field && prevState.sortDirection === "desc" ? "asc" : "desc"
        }));
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private convertToAnalyticsFilters = (): AnalyticsFilters => {
        const { appliedFilters } = this.state;
        return {
            startDate: appliedFilters.startDate,
            endDate: appliedFilters.endDate,
            attributionId: appliedFilters.attributionId,
            brandId: appliedFilters.brandId,
            deviceType: appliedFilters.deviceType
        };
    };

    private getExportData = (): ExportableData[] => {
        const { rows, selectedGroup } = this.state;

        if (rows.length === 0) {
            return [];
        }

        /* eslint-disable camelcase */
        return [{
            title: `Event Info - ${selectedGroup === "action" ? "Action" : "Custom Event Trigger"}`,
            columns: [
                { key: "group_value", label: selectedGroup === "action" ? "Action" : "Custom Event Trigger" },
                { key: "event_count", label: "Event Count", format: "number" },
                { key: "distinct_users", label: "Distinct Users", format: "number" },
                { key: "distinct_devices", label: "Distinct Devices", format: "number" },
                { key: "distinct_things", label: "Distinct Things", format: "number" }
            ],
            rows,
            metadata: {
                filters: this.convertToAnalyticsFilters(),
                pageKey: "event_info",
                customerSlug: this.props.customerConfig.slug
            }
        }];
        /* eslint-enable camelcase */
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Event Info",
            pageKey: "event_info",
            customerSlug: this.props.customerConfig.slug,
            filters: this.convertToAnalyticsFilters(),
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private getSortedRows(): EventInfoRow[] {
        const { rows, sortField, sortDirection } = this.state;
        return [...rows].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === "number" && typeof bVal === "number") {
                return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
            }

            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();
            if (sortDirection === "asc") {
                return aStr.localeCompare(bStr);
            }
            return bStr.localeCompare(aStr);
        });
    }

    private renderSortIcon(field: keyof EventInfoRow): string {
        const { sortField, sortDirection } = this.state;
        if (sortField !== field) return "";
        return sortDirection === "asc" ? " ▲" : " ▼";
    }

    private renderTable(): ReactNode {
        const { totals, isDarkTheme, selectedGroup, sidebarEntries } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        const sortedRows = this.getSortedRows();

        const groupLabel = selectedGroup === "action" ? "Action" : "Custom Event Trigger";

        const headerStyle: React.CSSProperties = {
            cursor: "pointer",
            userSelect: "none",
            padding: "10px 12px",
            borderBottom: `2px solid ${theme.border}`,
            color: theme.text,
            fontWeight: 600,
            fontSize: "13px",
            textAlign: "left",
            whiteSpace: "nowrap"
        };

        const cellStyle: React.CSSProperties = {
            padding: "8px 12px",
            borderBottom: `1px solid ${theme.border}`,
            color: theme.text,
            fontSize: "13px"
        };

        const numericCellStyle: React.CSSProperties = {
            ...cellStyle,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums"
        };

        const numericHeaderStyle: React.CSSProperties = {
            ...headerStyle,
            textAlign: "right"
        };

        const totalRowStyle: React.CSSProperties = {
            ...numericCellStyle,
            fontWeight: 700,
            borderTop: `2px solid ${theme.border}`,
            backgroundColor: isDarkTheme ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"
        };

        return (
            <WidgetCard
                title={`Event Info — ${groupLabel}`}
                description={sidebarEntries[0]?.description}
                theme={theme}
                isEmpty={sortedRows.length === 0}
                emptyMessage="No event data available for the selected filters."
            >
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={headerStyle} onClick={() => this.handleSort("group_value")}>
                                    {groupLabel}{this.renderSortIcon("group_value")}
                                </th>
                                <th style={numericHeaderStyle} onClick={() => this.handleSort("event_count")}>
                                    Event Count{this.renderSortIcon("event_count")}
                                </th>
                                <th style={numericHeaderStyle} onClick={() => this.handleSort("distinct_users")}>
                                    Distinct Users{this.renderSortIcon("distinct_users")}
                                </th>
                                <th style={numericHeaderStyle} onClick={() => this.handleSort("distinct_devices")}>
                                    Distinct Devices{this.renderSortIcon("distinct_devices")}
                                </th>
                                <th style={numericHeaderStyle} onClick={() => this.handleSort("distinct_things")}>
                                    Distinct Things{this.renderSortIcon("distinct_things")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((row, idx) => (
                                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "transparent" : (isDarkTheme ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)") }}>
                                    <td style={cellStyle}>{row.group_value ?? "(null)"}</td>
                                    <td style={numericCellStyle}>{row.event_count.toLocaleString()}</td>
                                    <td style={numericCellStyle}>{row.distinct_users.toLocaleString()}</td>
                                    <td style={numericCellStyle}>{row.distinct_devices.toLocaleString()}</td>
                                    <td style={numericCellStyle}>{row.distinct_things.toLocaleString()}</td>
                                </tr>
                            ))}
                            {totals && (
                                <tr>
                                    <td style={{ ...totalRowStyle, textAlign: "left", fontWeight: 700 }}>Totals</td>
                                    <td style={totalRowStyle}>{totals.event_count.toLocaleString()}</td>
                                    <td style={totalRowStyle}>{totals.distinct_users.toLocaleString()}</td>
                                    <td style={totalRowStyle}>{totals.distinct_devices.toLocaleString()}</td>
                                    <td style={totalRowStyle}>{totals.distinct_things.toLocaleString()}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </WidgetCard>
        );
    }

    public render(): ReactNode {
        const { filters, filterOptions, isDarkTheme, isLoading, error, selectedGroup, showScheduleModal, sidebarOpen, sidebarEntries } = this.state;

        return (
            <div ref={this.containerRef} style={{ display: "flex", alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Container className="mw-100" key="event-info" style={{ position: "relative", paddingBottom: "40px" }}>
                    <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                        <ExportButton
                            context={this.getExportContext()}
                            enabled={!isLoading && !error && this.state.rows.length > 0}
                            scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                            onScheduleClick={() => this.setState({ showScheduleModal: true })}
                        />
                        <Button
                            onClick={this.toggleSidebar}
                            style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px" }}
                            title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                        >
                            <BookHalf />
                        </Button>
                        <Button
                            onClick={this.toggleTheme}
                            style={{ backgroundColor: "transparent", border: "none", fontSize: "24px", padding: "4px 8px" }}
                            title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                        >
                            {isDarkTheme ? <Sun /> : <Moon />}
                        </Button>
                    </div>

                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>Event Info</h2>
                        </Col>
                    </Row>

                    <AnalyticsFiltersComponent
                        filters={filters}
                        filterOptions={filterOptions}
                        onFilterChange={this.handleFilterChange}
                        onApplyFilters={this.handleApplyFilters}
                        customerConfig={this.props.customerConfig}
                        enableGroupBy={{}}
                    />

                    <Row style={{ marginTop: "16px", marginBottom: "8px" }}>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Group By</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={selectedGroup}
                                    onChange={(e) => this.handleGroupChange(e.target.value as GroupType)}
                                >
                                    <option value="action">Action</option>
                                    <option value="custom_event_trigger">Custom Event Trigger</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>

                    {isLoading ? (
                        <Row className="justify-content-center">
                            <Col className="text-center" style={{ padding: "32px" }}>
                                <Spinner animation="border" variant="primary" />
                            </Col>
                        </Row>
                    ) : error ? (
                        <Row>
                            <Col>
                                <Alert variant="danger" style={{ marginTop: "20px" }}>{error}</Alert>
                            </Col>
                        </Row>
                    ) : (
                        <Row style={{ marginTop: "20px" }}>
                            <Col md={12}>
                                {this.renderTable()}
                            </Col>
                        </Row>
                    )}

                    <ScheduleExportModal
                        show={showScheduleModal}
                        onHide={() => this.setState({ showScheduleModal: false })}
                        filters={this.convertToAnalyticsFilters()}
                        pageKey="event_info"
                        pageTitle="Event Info"
                        customerSlug={this.props.customerConfig.slug}
                        editingSchedule={null}
                        onSave={this.handleScheduleSave}
                    />
                </Container>
              </div>
              <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            </div>
        );
    }
}

export default EventInfo;
